import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { cn } from "@ncp/ui";
import { thresholdWords, useCalculationMethod } from "../case/figures";

/** The provision a band of the published method applies, and where the Department's verification of it stands (GET /api/analysis/method/legal-bases). */
export interface MethodLegalBasis {
  key: string;
  name: string;
  nameAr: string;
  bands: string[];
  attribution: string;
  attributionAr: string;
  status: "Verified" | "VerificationRequired" | "AwaitingApproval" | string;
  gazetteReference?: string | null;
}

export function useMethodLegalBases() {
  const api = useApi();
  return useQuery({ queryKey: ["analysis", "method", "legal-bases"], queryFn: () => api.get<MethodLegalBasis[]>("/api/analysis/method/legal-bases"), staleTime: 10 * 60_000 });
}

/**
 * Beside the Data Bank's dominance mark: the dominance presumption it applies, as the legal rules register cites it, and whether the Department
 * has verified it against the Official Gazette (condition 2.13). Nothing until the register has answered.
 */
export function DominanceBasis({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const words = thresholdWords(useCalculationMethod().data?.bands, locale);
  const basis = useMethodLegalBases().data?.find((b) => b.key === "dominance.presumption");
  if (!basis) return null;
  return (
    <p className={cn("text-[11px] leading-relaxed", basis.status === "Verified" ? "text-muted-foreground" : "text-warning-800", className)} dir="auto">
      {t("backoffice:dataBank.intel.dominanceBasis", {
        share: words.share,
        basis: locale === "ar" ? basis.attributionAr : basis.attribution,
        status: t(`backoffice:decisionControls.readiness.status.${basis.status}`, { defaultValue: basis.status }),
      })}
    </p>
  );
}
